import { useShadow, useShadowStyle } from '../../util/wcUtil.ts';
import '../inputs/buttons/as-button.ts';
import '../base/as-spinner.ts';
import '../text-and-lang/as-text.ts';
import styleText from './as-modal-export-progress.css?raw';
import { requestExportCancel } from '../../features/importExport/lib/exportBuilder.ts';
import type { ExportStatusDetail, ExportProgressDetail, ExportUncancellableDetail } from '../../data/data-interfaces.ts';

/**
 * <as-modal-export-progress>
 *
 * Progress panel for a running export. Opens on the pending as-export-status
 * event and closes when the export settles.
 *
 * Cancel asks the export to stop (requestExportCancel) rather than just hiding
 * the panel. An export can only stop between steps, so the step already running
 * finishes first — the button shows a spinner and "Cancelling…" until the
 * export confirms with a cancelled status, and the panel closes then.
 *
 * Cancel is live as soon as the panel opens and stays usable for the export's
 * cancel grace period (see CANCEL_GRACE_PERIOD_MS) — the export itself holds at
 * its point of no return until that window is up, so the panel cannot flash
 * past before the button can be reached. Once the export commits it says so:
 * heading changes to "Finishing up…", spinner, no Cancel.
 */

const HEADINGS = {
  exporting:  { dictKey: 'modals.export.progress.heading',        text: 'Exporting…' },
  cancelling: { dictKey: 'modals.export.progress.cancelling',     text: 'Cancelling…' },
  finishing:  { dictKey: 'modals.export.progress.finishing',      text: 'Finishing up…' },
  failed:     { dictKey: 'modals.export.progress.failedHeading',  text: 'Export failed' },
} as const;


export class AsModalExportProgress extends HTMLElement {
  static readonly tag = 'as-modal-export-progress' as const;

  /** Exporter the open panel belongs to, taken from the pending event. */
  private _exporterId: string | null = null;
  /** True between the Cancel click and the export confirming it stopped. */
  private _cancelling = false;
  /** True once an error is on show — the button is a plain dismiss from then on. */
  private _failed = false;

  private _onExportStatus = (e: Event): void => {
    const detail = (e as CustomEvent<ExportStatusDetail>).detail;
    if (detail.state === 'pending') {
      this._exporterId = detail.exporterId;
      this._reset();
      this._overlay()?.open(this.id);
    } else if (detail.state === 'cancelled') {
      this._clearCancelling();
      this._closeIfShowing();
    } else if (detail.state === 'success' && detail.operation !== 'grabcad') {
      this._clearCancelling();
      this._closeIfShowing();
    } else if (detail.state === 'error') {
      this._setError(detail.message);
    }
    // grabcad success: as-modal-grabcad-success opens itself on the same event,
    // which switches the overlay's visible-child away from this panel.
  };

  private _onExportProgress = (e: Event): void => {
    const { current, total } = (e as CustomEvent<ExportProgressDetail>).detail;
    this._setProgress(current, total);
  };

  /**
   * The export has committed and can no longer be stopped, so the Cancel button
   * goes rather than sitting there promising something it can't deliver. The
   * panel switches to "Finishing up…" with a spinner: the file count is done
   * with, and what's left is a wait on the host with no progress to report.
   */
  private _onExportUncancellable = (e: Event): void => {
    const { exporterId } = (e as CustomEvent<ExportUncancellableDetail>).detail;
    if (this._exporterId !== null && exporterId !== this._exporterId) return;
    const sr = this.shadowRoot;
    if (!sr) return;
    this._clearCancelling();
    this._setHeading(HEADINGS.finishing);
    (sr.querySelector('.progress-bar') as HTMLElement).hidden = true;
    (sr.querySelector('.progress-label') as HTMLElement).hidden = true;
    (sr.querySelector('.finishing-spinner') as HTMLElement).hidden = false;
    // The whole row, not just the button — an empty flex child still takes its
    // share of the panel's gap and leaves a hole where the button was.
    this._footer().hidden = true;
  };

  connectedCallback(): void {
    useShadow(this, /*html*/`
      <div class="modal-panel">
        <p class="heading">
          <as-text class="heading-text" dict-key="${HEADINGS.exporting.dictKey}" default-text="${HEADINGS.exporting.text}"></as-text>
          <as-spinner class="finishing-spinner" hidden></as-spinner>
        </p>
        <progress class="progress-bar" value="0" max="1"></progress>
        <p class="progress-label"></p>
        <p class="error-msg" hidden></p>
        <div class="footer-actions">
          <as-button name="cancel"
            dict-key="modals.export.progress.cancelButton" default-text="Cancel export"
            pending-dict-key="${HEADINGS.cancelling.dictKey}" pending-default-text="${HEADINGS.cancelling.text}"></as-button>
        </div>
      </div>
    `);
    useShadowStyle(this, styleText, './as-modal-export-progress.css');
    this.shadowRoot!.addEventListener('as-button-click', this._onCancelClick);
    window.addEventListener('as-export-status', this._onExportStatus);
    window.addEventListener('as-export-progress', this._onExportProgress);
    window.addEventListener('as-export-uncancellable', this._onExportUncancellable);
  }

  disconnectedCallback(): void {
    window.removeEventListener('as-export-status', this._onExportStatus);
    window.removeEventListener('as-export-progress', this._onExportProgress);
    window.removeEventListener('as-export-uncancellable', this._onExportUncancellable);
  }

  private _onCancelClick = (): void => {
    // Once the export has failed there's nothing left to stop — the button is
    // just a dismiss.
    if (this._failed) {
      this._closeIfShowing();
      return;
    }
    if (this._cancelling) return;
    this._cancelling = true;
    this._setHeading(HEADINGS.cancelling);
    // Spinner + "Cancelling…" until the export confirms it stopped.
    this._button()?.setAttribute('pending', '');
    requestExportCancel(this._exporterId ?? undefined);
  };

  private _overlay(): any {
    return this.closest('as-modal-overlay') ?? document.querySelector('#as-app-overlay');
  }

  private _button(): HTMLElement | null {
    return this.shadowRoot?.querySelector('as-button') ?? null;
  }

  /** The row holding the Cancel button. */
  private _footer(): HTMLElement {
    return this.shadowRoot!.querySelector('.footer-actions') as HTMLElement;
  }

  /**
   * Close the overlay only while this panel is the one on show. An export
   * settles some time after the user acted on it, and by then they may have
   * opened something else — closing that would be wrong.
   */
  private _closeIfShowing(): void {
    const overlay = this._overlay();
    if (overlay?.visibleChildId === this.id) overlay.close();
  }

  /** Leave the "Cancelling…" state: spinner off, heading back to exporting. */
  private _clearCancelling(): void {
    if (!this._cancelling) return;
    this._cancelling = false;
    this._button()?.removeAttribute('pending');
    this._setHeading(HEADINGS.exporting);
  }

  /** Point the heading's as-text at one of HEADINGS, so it keeps translating. */
  private _setHeading(heading: { dictKey: string; text: string }): void {
    const el = this.shadowRoot?.querySelector('.heading-text');
    if (!el) return;
    el.setAttribute('dict-key', heading.dictKey);
    el.setAttribute('default-text', heading.text);
  }

  private _reset(): void {
    const sr = this.shadowRoot;
    if (!sr) return;
    this._cancelling = false;
    this._failed = false;
    this._setHeading(HEADINGS.exporting);
    const bar = sr.querySelector('.progress-bar') as HTMLProgressElement;
    bar.value = 0;
    bar.max = 1;
    bar.hidden = false;
    (sr.querySelector('.progress-label') as HTMLElement).textContent = '';
    (sr.querySelector('.progress-label') as HTMLElement).hidden = false;
    (sr.querySelector('.finishing-spinner') as HTMLElement).hidden = true;
    (sr.querySelector('.error-msg') as HTMLElement).hidden = true;
    // Cancel is live from the off. It used to be disabled for the first five
    // seconds, from when clicking it only hid the panel and would have left the
    // export running unseen. It stops the export now, and the export holds its
    // point of no return open for the grace period, so the button is usable for
    // as long as it can actually do anything.
    this._button()?.removeAttribute('pending');
    this._footer().hidden = false; // a previous export may have taken it away
  }

  private _setProgress(current: number, total: number): void {
    const sr = this.shadowRoot;
    if (!sr) return;
    const bar = sr.querySelector('.progress-bar') as HTMLProgressElement;
    const label = sr.querySelector('.progress-label') as HTMLElement;
    if (bar) { bar.value = current; bar.max = Math.max(total, 1); }
    if (label) label.textContent = total > 0 ? `${current} of ${total} file${total !== 1 ? 's' : ''}` : '';
  }

  private _setError(message?: string): void {
    const sr = this.shadowRoot;
    if (!sr) return;
    this._cancelling = false;
    this._failed = true;
    this._setHeading(HEADINGS.failed);
    (sr.querySelector('.progress-bar') as HTMLElement).hidden = true;
    (sr.querySelector('.progress-label') as HTMLElement).hidden = true;
    (sr.querySelector('.finishing-spinner') as HTMLElement).hidden = true;
    const errEl = sr.querySelector('.error-msg') as HTMLElement;
    errEl.textContent = message ?? 'An error occurred during export.';
    errEl.hidden = false;
    this._button()?.removeAttribute('pending');
    // The button is the dismiss from here on, so the row has to come back even
    // if the export got past the point of no return before failing.
    this._footer().hidden = false;
  }
}

if (!customElements.get(AsModalExportProgress.tag)) {
  customElements.define(AsModalExportProgress.tag, AsModalExportProgress);
}
